import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { shallowEqual, type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import amplitudeAnalyticsSettings from '@alltrails/amplitude/slices/amplitudeAnalyticsSettings';
import context from '@alltrails/context/slices/contextSlice';
import currencyCode from '@alltrails/context/slices/currencyCodeSlice';
import plans from '@alltrails/context/slices/plansSlice';
import type RequiredPreloadedState from '@alltrails/core/types/RequiredPreloadedState';
import { experimentsReducer as experiments } from '@alltrails/experiments';
import features from '@alltrails/features/slices/features';
import installBanner, { InstallBannerState } from '@alltrails/install-prompt/slices/installBanner';
import lists, { type ListsState } from '@alltrails/lists/slices/lists';
import { baseApi } from '@alltrails/redux-helpers';
import algoliaConfigs from '@alltrails/search/slices/algoliaConfigs';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import type Context from '@alltrails/types/Context';
import type CountryCode from '@alltrails/types/CountryCode';
import sections, { type SectionsState } from '../slices/sections';
import { listenerMiddleware } from './listenerMiddleware';

export type PreloadedState = RequiredPreloadedState & {
  algoliaConfigs: AlgoliaConfigs;
  installBanner?: InstallBannerState;
  lists?: ListsState;
  sections?: SectionsState;
};

export const reducer = combineReducers({
  algoliaConfigs,
  amplitudeAnalyticsSettings,
  [baseApi.reducerPath]: baseApi.reducer,
  browser: (state = {}) => state,
  context,
  currencyCode,
  experiments,
  features,
  installBanner,
  irclickid: (state = '') => state,
  lists,
  plans,
  requestCountryCode: (state = '' as CountryCode) => state,
  sections
});

export const buildStore = (preloadedState?: PreloadedState) =>
  configureStore({
    reducer,
    preloadedState,
    middleware: getDefaultMiddleware => getDefaultMiddleware().concat(baseApi.middleware, listenerMiddleware.middleware)
  });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']> & { context?: Context };

// these are intended to use within the app only
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any) =>
  useSelector<RootState, any>(selector, shallowEqual);
